import {useState, useMemo, useCallback} from 'react'
import {Plus} from 'lucide-react'
import {useNavigate} from 'react-router-dom'
import {useQuery} from '@tanstack/react-query'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {useAuth} from '@/contexts/auth-context'
import {useServices} from '@/services/context'
import {Button} from '@/components/ui/button'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {Input} from '@/components/ui/input'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {Pagination} from '@/components/ui/pagination'
import {Icon} from '@/components/shared/Icon'
import {InlineRow} from '@/components/shared/InlineRow'
import {MutedText} from '@/components/shared/MutedText'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {TextLink} from '@/components/shared/TextLink'
import {EmptyState} from '@/components/shared/EmptyState'
import {PageLayout} from '@/components/shared/PageLayout'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {DialogForm} from '@/components/shared/DialogForm'
import {FormField} from '@/components/shared/FormField'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {useServerPagination} from '@/hooks/use-server-pagination'
import {ServiceAccountControllerListEndpoint} from '@/types/endpoint-contracts'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {Textarea} from '@/components/ui/textarea'
import {useDialog} from '@/hooks/useDialog'
import {CONCEPT_ICON} from '@/lib/concepts'
import {permissions} from '@/lib/permissions'
import {routes} from '@/lib/routes'
import {formatDateOnly} from '@/lib/date-utils'
import {ApiError} from '@/api/client'
import {useCreateServiceAccount, SERVICE_ACCOUNTS_KEY} from '@/features/service-accounts/hooks/useServiceAccounts'
import type {ServiceAccount} from '@/types/service-account'
import type {Page} from '@/types/page'

export function ServiceAccounts() {
    const i18n = useI18n()
    const navigate = useNavigate()
    const services = useServices()
    const {hasPermission} = useAuth()
    usePageTitle(i18n.serviceAccounts.pageTitle as string)

    const canView = hasPermission(permissions.serviceAccounts.view)
    const canCreate = hasPermission(permissions.serviceAccounts.create)

    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: ServiceAccountControllerListEndpoint,
        defaultSortBy: ServiceAccountControllerListEndpoint.sort.NAME,
    })

    const hasFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
            || Object.keys(searchAndFilters.dynamicAttributes).length > 0
    }, [searchAndFilters])

    const {data, isLoading, error, refetch} = useQuery<Page<ServiceAccount>>({
        queryKey: [SERVICE_ACCOUNTS_KEY, queryString],
        queryFn: () => services.serviceAccounts.list(Object.fromEntries(new URLSearchParams(queryString))),
        enabled: canView,
    })

    const items = data?.content ?? []
    const totalItems = data?.totalElements ?? 0
    const totalPages = data?.totalPages ?? 0

    const createDialog = useDialog()
    const [createName, setCreateName] = useState('')
    const [createDescription, setCreateDescription] = useState('')
    const [createNameError, setCreateNameError] = useState<string | null>(null)
    const createMutation = useCreateServiceAccount()

    const resetCreateForm = useCallback(() => {
        setCreateName('')
        setCreateDescription('')
        setCreateNameError(null)
    }, [])

    const onCreateOpenChange = useCallback((open: boolean) => {
        if (!open) resetCreateForm()
        createDialog.bind.onOpenChange(open)
    }, [createDialog.bind, resetCreateForm])

    const submitCreate = useCallback((e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        const trimmed = createName.trim()
        if (!trimmed) {
            setCreateNameError(i18n.serviceAccounts.nameRequired as string)
            return
        }
        if (trimmed.length > 100) {
            setCreateNameError(i18n.serviceAccounts.nameTooLong as string)
            return
        }
        if (createDescription.length > 500) {
            setCreateNameError(i18n.serviceAccounts.descriptionTooLong as string)
            return
        }
        setCreateNameError(null)
        createMutation.mutate(
            {name: trimmed, description: createDescription.trim() || null},
            {
                onSuccess: (sa) => {
                    resetCreateForm()
                    createDialog.close()
                    navigate(routes.admin.serviceAccounts.detail(sa.id))
                },
                onError: (err) => {
                    if (err instanceof ApiError && err.status === 409) {
                        setCreateNameError(i18n.serviceAccounts.duplicateNameError as string)
                    }
                },
            },
        )
    }, [createName, createDescription, createMutation, createDialog, i18n.serviceAccounts, navigate, resetCreateForm])

    const filterControls = (
        <UnifiedSearchBar
            contract={ServiceAccountControllerListEndpoint}
            value={searchAndFilters}
            onChange={setSearchAndFilters}
            placeholder={i18n.serviceAccounts.filterByName as string}
        />
    )

    const emptyState = (
        <EmptyState
            data-testid="empty-state"
            title={i18n.serviceAccounts.emptyTitle as string}
            description={i18n.serviceAccounts.emptyDescription as string}
            action={canCreate ? (
                <Button onClick={createDialog.open} data-testid="btn-create-empty">
                    <Icon icon={Plus} className="mr-2"/>
                    {i18n.serviceAccounts.createButton}
                </Button>
            ) : undefined}
        />
    )

    return (
        <>
        <PageLayout
            variant="list"
            icon={CONCEPT_ICON.serviceAccount}
            title={i18n.serviceAccounts.listTitle as string}
            count={totalItems}
            description={`${i18n.serviceAccounts.listCount({count: totalItems})}${hasFilters ? ` ${i18n.serviceAccounts.matchingFilters}` : ''}`}
            data-testid="service-accounts-list"
            loading={isLoading}
            error={error}
            onRetry={() => refetch()}
            actions={canCreate ? (
                <Button onClick={createDialog.open} data-testid="btn-create-service-account">
                    <Icon icon={Plus} className="mr-2"/>
                    {i18n.serviceAccounts.createButton}
                </Button>
            ) : undefined}
            filters={filterControls}
            emptyState={emptyState}
            pagination={totalItems > 0 ? (
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                />
            ) : undefined}
        >
            <Table data-help-prefix="serviceAccounts">
                <TableHeader>
                    <TableRow data-readonly>
                        <TableHead data-testid="col-name" data-help-key="name">
                            {i18n.serviceAccounts.columns.name}
                        </TableHead>
                        <TableHead data-testid="col-active-keys" data-help-key="activeKeys">
                            {i18n.serviceAccounts.columns.activeKeys}
                        </TableHead>
                        <TableHead data-testid="col-status" data-help-key="status">
                            {i18n.serviceAccounts.columns.status}
                        </TableHead>
                        <TableHead data-testid="col-created" data-help-key="created">
                            {i18n.serviceAccounts.columns.created}
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {items.map((sa) => (
                        <ClickableTableRow
                            key={sa.id}
                            to={routes.admin.serviceAccounts.detail(sa.id)}
                            data-testid={`service-account-row-${sa.id}`}
                        >
                            <TableCell>
                                <InlineRow>
                                    <TextLink
                                        to={routes.admin.serviceAccounts.detail(sa.id)}
                                        testId={`link-service-account-${sa.id}`}
                                        stopPropagation
                                        variant="bold"
                                    >
                                        {sa.name}
                                    </TextLink>
                                </InlineRow>
                                {sa.description && (
                                    <MutedText size="sm" block>{sa.description}</MutedText>
                                )}
                            </TableCell>
                            <TableCell>
                                <Badge
                                    variant="outline"
                                    data-testid={`badge-active-keys-${sa.id}`}
                                >
                                    {sa.activeKeyCount}
                                </Badge>
                            </TableCell>
                            <TableCell>
                                <Badge
                                    variant={sa.active ? BadgeVariant.SUCCESS : BadgeVariant.SECONDARY}
                                    data-testid={`badge-status-${sa.id}`}
                                >
                                    {sa.active
                                        ? i18n.serviceAccounts.statusActive
                                        : i18n.serviceAccounts.statusInactive}
                                </Badge>
                            </TableCell>
                            <TableCell>
                                <MutedText>{formatDateOnly(sa.createdAt)}</MutedText>
                            </TableCell>
                        </ClickableTableRow>
                    ))}
                    {items.length === 0 && (
                        <TableEmptyState
                            colSpan={4}
                            hasFilters={hasFilters}
                            matchMessage={i18n.serviceAccounts.emptyMatching as string}
                            emptyMessage={i18n.serviceAccounts.emptyDescription as string}
                        />
                    )}
                </TableBody>
            </Table>

        </PageLayout>

        <Dialog open={createDialog.isOpen} onOpenChange={onCreateOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle data-testid="create-dialog-title">
                        {i18n.serviceAccounts.createTitle}
                    </DialogTitle>
                    <DialogDescription>
                        {i18n.serviceAccounts.pageDescription}
                    </DialogDescription>
                </DialogHeader>
                <DialogForm onSubmit={submitCreate} data-testid="create-form">
                    <FormField
                        label={i18n.serviceAccounts.nameLabel as string}
                        htmlFor="sa-create-name"
                        required
                        error={createNameError ?? undefined}
                    >
                        <Input
                            id="sa-create-name"
                            data-testid="create-input-name"
                            value={createName}
                            onChange={(e) => setCreateName(e.target.value)}
                            maxLength={100}
                            autoFocus
                        />
                    </FormField>
                    <FormField
                        label={i18n.serviceAccounts.descriptionLabel as string}
                        htmlFor="sa-create-description"
                    >
                        <Textarea
                            id="sa-create-description"
                            data-testid="create-input-description"
                            value={createDescription}
                            onChange={(e) => setCreateDescription(e.target.value)}
                            placeholder={i18n.serviceAccounts.descriptionPlaceholder as string}
                            maxLength={500}
                            rows={3}
                        />
                    </FormField>
                    <SaveCancelFooter
                        testIdPrefix="create"
                        onCancel={createDialog.close}
                        saving={createMutation.isPending}
                        saveLabel={i18n.serviceAccounts.createButton as string}
                        savingLabel={i18n.serviceAccounts.createButton as string}
                    />
                </DialogForm>
            </DialogContent>
        </Dialog>
        </>
    )
}
